import "./style.css";
import React from "react";
import Button from "components/Buttons";
import Loader from "components/Spinners/Loader";
import { gql, useQuery } from "@apollo/client";
import { LIST_HEADER } from "apollo/queries/ListHeader";
import { useLocation } from "react-router-dom";
import { useLoggedIn } from "stores/auth/authStore";
import { useAuthStore } from "stores/auth/authStore";

const HeroSection = ({ scrollRef  }) => {
  const location = useLocation();
  const isBuyRoute = location?.pathname === "/buy";
  const isCactusRoute = location?.pathname === "/cactus";
  const isBillyCookRoute = location?.pathname === "/billy-cook";

  const cactusBanner = {
    image:
      "https://www.cactussaddlery.com/cdn/shop/files/cs-slideshow-1800x800_95c7a999-5933-4732-885d-a46768cdc04f_2000x.jpg?v=1693280293",
    subtitle: "ESTABLISHED IN 2003",
    title: "CUSTOM HANDCRAFTED WESTERN SADDLES",
    description: "Every saddle is hand-crafted in Greenville, TX.",
  };

  const billyCookBanner = {
    image:
      "https://cdn.shopify.com/s/files/1/0731/3188/4798/files/product-saddle-10-2181-1-_1.jpg?v=1757923228&width=3840",
    subtitle: "Handcrafted in Oklahoma since 1953",
    title: "Genuine Billy Cook",
    description:
      "Western saddles and tack rooted in tradition—built for riders who live in the saddle.",
  };
  const { user } = useLoggedIn();
  const fetchFullUserProfile = useAuthStore((s) => s.fetchFullUserProfile);

  const parsedBusinessForm = React.useMemo(() => {
    const raw = user?.businessForm;
    if (!raw) return null;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return raw;
  }, [user?.businessForm]);

  React.useEffect(() => {
    if (!isBuyRoute) {
      const hasBrandingLogo = Boolean(parsedBusinessForm?.branding?.logoUrl);
      const hasBanner = Boolean(user?.banner);
      if (!user || !hasBrandingLogo || !hasBanner) {
        fetchFullUserProfile?.();
      }
    }
  }, [isBuyRoute, user, parsedBusinessForm, fetchFullUserProfile]);

  const { data, loading } = useQuery(gql(LIST_HEADER), {
    variables: { filter: { type: "BUY_SADDLE" } },
    skip: !isBuyRoute,
  });

  const headerData = isBuyRoute
    ? data?.listHeader?.[0]
    : isCactusRoute
    ? cactusBanner
    : isBillyCookRoute
    ? billyCookBanner
    : user?.banner;

  const backgroundImage = headerData?.image || null;

  const backgroundVideo = headerData?.video;

  const logoUrl = parsedBusinessForm?.branding?.logoUrl || null;

  const renderDynamicContent = () => {
    if (isBuyRoute && loading) {
      return (
        <Loader
          filledColor="#2b364b"
          className="!h-[50px] !animate-spin !flex !items-center !mt-[-6px] !text-center !justify-center"
        />
      );
    }

    if (!headerData) return null;

    return (
      <>
        {logoUrl && (
          <img
            src={logoUrl}
            alt="brand-logo"
            className="mb-2 w-[100px] sm:w-[140px] md:w-[180px] lg:w-[200px] object-contain"
            loading="lazy"
          />
        )}
        {headerData.subtitle && (
          <p className="cactus_subtitle">{headerData.subtitle}</p>
        )}
        <h2 className="cactus_title">{headerData.title}</h2>
        <p className="cactus_description">{headerData.description}</p>
        {headerData.btnLabel && (
          <Button
            className="cactus_story_btn"
            onClick={() => scrollRef?.current?.scrollIntoView({ behavior: "smooth" })}
          >
            {headerData.btnLabel}
          </Button>
        )}
      </>
    );
  };

  return (
    <div
      className={`flex justify-center items-center h-[695px] md:h-[943px] lg:h-[37.875rem] ${
        isBillyCookRoute ? "billycook-hero-wrap" : ""
      }`}
      style={{
        backgroundImage: backgroundVideo ? "none" : `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: isBillyCookRoute ? "center 35%" : "center",
        backgroundRepeat: "no-repeat",
        width: "100%",
      }}
    >
      {backgroundVideo && (
        <video
          preload="metadata"
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-[695px] md:h-[943px] lg:h-[37.875rem] object-cover top-0 lg:top-[95px]"
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      )}
      <div
        className={`relative z-10 flex flex-col justify-center items-center gap-y-2 ${
          isBillyCookRoute ? "billycook-hero-text" : ""
        }`}
      >
        {renderDynamicContent()}
      </div>
    </div>
  );
};

export default HeroSection;
